/**
 * IC Bingo — Draw Engine
 *
 * Pure, framework-free logic for advancing the pre-generated 90-ball sequence.
 * The Host Panel calls this module; the Projector never touches it directly.
 *
 * Fairness rules:
 *   - The `drawSequence` (a shuffled permutation of 1..90) is generated ONCE
 *     by the backend at game creation and is IMMUTABLE for the life of the game.
 *   - `drawIndex` is a pointer into that sequence.
 *   - callNext: reveals drawSequence[drawIndex] and advances the pointer.
 *   - undo    : rewinds the pointer by 1 and pops the last called number.
 *   - Because we do NOT regenerate a random number on redo, calling next
 *     after an undo reveals the EXACT same number that was undone.
 *   - Up to `MAX_UNDO` (3) consecutive undos are permitted. This is tracked
 *     as `undoDepth`; each callNext decrements it toward 0.
 */

export const MAX_UNDO = 3;
export const BALLS = 90;

/**
 * Factory: build the initial draw state.
 * All fields are pure data; no methods, no side effects.
 */
export function createDrawState({
  drawSequence,
  drawIndex = 0,
  called = [],
  undoDepth = 0,
} = {}) {
  if (!Array.isArray(drawSequence)) {
    throw new Error("createDrawState: drawSequence must be an array");
  }
  return {
    drawSequence: [...drawSequence],
    drawIndex,
    called: [...called],
    undoDepth,
  };
}

export function canCallNext(state) {
  return state.drawIndex < state.drawSequence.length;
}

export function canUndo(state) {
  return state.drawIndex > 0 && state.undoDepth < MAX_UNDO;
}

/**
 * Reveal the next number.
 * Returns { state, revealed } where `revealed` is the number just called
 * or `null` if the game has already exhausted all 90 balls.
 */
export function callNext(state, { now = Date.now } = {}) {
  if (!canCallNext(state)) {
    return { state, revealed: null };
  }
  const value = state.drawSequence[state.drawIndex];
  const called = [...state.called, { value, calledAt: now() }];
  const drawIndex = state.drawIndex + 1;
  // If we had outstanding undos, this call consumes one of them (redo).
  const undoDepth = Math.max(0, state.undoDepth - 1);
  return {
    state: { ...state, drawIndex, called, undoDepth },
    revealed: value,
  };
}

/**
 * Rewind by one number.
 * Returns { state, removed } where `removed` is the value that was pulled
 * off the called list, or `null` if the undo was refused (no history or
 * MAX_UNDO already reached).
 */
export function undo(state) {
  if (!canUndo(state)) {
    return { state, removed: null };
  }
  const last = state.called[state.called.length - 1];
  const removed = last.value;
  const called = state.called.slice(0, -1);
  const drawIndex = state.drawIndex - 1;
  const undoDepth = state.undoDepth + 1;
  return {
    state: { ...state, drawIndex, called, undoDepth },
    removed,
  };
}

/**
 * Read helpers.
 */
export function currentAndPrevious(state) {
  const n = state.called.length;
  return {
    current: n > 0 ? state.called[n - 1].value : null,
    previous: n > 1 ? state.called[n - 2].value : null,
  };
}

export function calledSet(state) {
  return new Set(state.called.map((c) => c.value));
}

export function peekNext(state) {
  return canCallNext(state) ? state.drawSequence[state.drawIndex] : null;
}

export function remainingCount(state) {
  return state.drawSequence.length - state.drawIndex;
}

export function calledCount(state) {
  return state.called.length;
}
